import type { ExtraFieldName, ExtraFieldValue, ExtraFieldsValues } from '@ts-lists/types'
import type { TasksKanbanStore } from './interfaces'

import { useListsStore } from '@stores/lists'

export const getters = {
  getTasks: (state: TasksKanbanStore): TasksKanbanStore['tasks'] => state.tasks,
  getAgents: (state: TasksKanbanStore): TasksKanbanStore['agents'] => state.agents,
  getCustomer: (state: TasksKanbanStore): TasksKanbanStore['customer'] => state.customer,
  getRelatedTo: (state: TasksKanbanStore): TasksKanbanStore['relatedTo'] => state.relatedTo,
  getTaskType: (state: TasksKanbanStore): TasksKanbanStore['taskType'] => state.taskType,
  hasTaskType: (state: TasksKanbanStore): boolean => !!state.taskType,
  getTeam: (state: TasksKanbanStore): TasksKanbanStore['team'] => state.team,
  getStage: (state: TasksKanbanStore): TasksKanbanStore['stage'] => state.stage,
  getPriority: (state: TasksKanbanStore): TasksKanbanStore['priority'] => state.priority,
  getDeadline: (state: TasksKanbanStore): TasksKanbanStore['deadline'] => state.deadline,
  getCreatedAt: (state: TasksKanbanStore): TasksKanbanStore['createdAt'] => state.createdAt,
  getUpdatedAt: (state: TasksKanbanStore): TasksKanbanStore['updatedAt'] => state.updatedAt,
  getAssignee: (state: TasksKanbanStore): TasksKanbanStore['assignee'] => state.assignee,
  getCreator: (state: TasksKanbanStore): TasksKanbanStore['creator'] => state.creator,
  getWaitingReason: (state: TasksKanbanStore): TasksKanbanStore['waitingReason'] => state.waitingReason,
  getResolution: (state: TasksKanbanStore): TasksKanbanStore['resolution'] => state.resolution,
  getCustomerOutcome: (state: TasksKanbanStore): TasksKanbanStore['customerOutcome'] => state.customerOutcome,
  getCancelReason: (state: TasksKanbanStore): TasksKanbanStore['cancelReason'] => state.cancelReason,
  getSubmitted: (state: TasksKanbanStore): TasksKanbanStore['submitted'] => state.submitted,
  getComment: (state: TasksKanbanStore): TasksKanbanStore['comment'] => state.comment,
  getLogs: (state: TasksKanbanStore): TasksKanbanStore['logs'] => state.logs,
  getLogsPagination: (state: TasksKanbanStore): TasksKanbanStore['logsPagination'] => state.logsPagination,
  getHasMoreLogs: (state: TasksKanbanStore): boolean => !!state.logsPagination.next_cursor,
  getExtraField: (state: TasksKanbanStore) => (name: ExtraFieldName): ExtraFieldValue => {
    return state.taskType
      ? (state.extraFieldsByTaskType[state.taskType]?.[name] ?? null)
      : null
  },
  getExtraFieldsPayload: (state: TasksKanbanStore): ExtraFieldsValues => {
    if (!state.taskType) return {}
    return state.extraFieldsByTaskType[state.taskType] || {}
  },

  isExtraFieldsValid: (state: TasksKanbanStore): boolean => {
    if (!state.taskType) return false

    const listsStore = useListsStore()
    const taskTypeOptions = listsStore.getTaskTypes?.[state.taskType]
    if (!taskTypeOptions) return true

    const extraFields = taskTypeOptions.extra_fields || []
    if (!extraFields.length) return true

    const optionalFields = taskTypeOptions.optional_fields || []
    const extraFieldsConfig = listsStore.getExtraFields
    const values = state.extraFieldsByTaskType[state.taskType] || {}

    return extraFields.every((name) => {
      if (optionalFields.includes(name)) return true

      const isRequired = extraFieldsConfig?.[name]?.required
      if (!isRequired) return true

      const value = values[name]
      if (Array.isArray(value)) return value.length > 0

      return value !== null && value !== undefined && value !== ''
    })
  },
}
